import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  computed,
  inject,
  input
} from "@angular/core";
import { dateUtil } from "src/app/support/date-util";
import { DxFormComponent } from "devextreme-angular";
import {
  clone,
  every,
  get,
  isArray,
  isBoolean,
  isDate,
  isEqual,
  keys,
  sortBy,
  toPairs
} from "lodash-es";
import { Subscription } from "rxjs";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { nextRuntimeId } from "src/app/support/runtime-id";
import { PageEditActionComponent } from "./page-edit-action.component";
import { PageEditFormStatus } from "./page-edit-form-status";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { ItemClickEvent } from "devextreme/ui/drop_down_button";
import { BeforeUnloadService } from "src/app/services/before-unload.service";
import { PageEditStatusContentDirective } from "src/app/components/ui/page/page-edit/page-edit-status-content.directive";

export type PageEditLayoutType = "center" | "default" | "no-grid-full-center";

export type PageEditSize = "sm" | "base" | "lg";

@Component({
  selector: "snoots-page-edit",
  templateUrl: "page-edit.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex: 1;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PageEditComponent implements AfterContentInit, OnDestroy {
  @ContentChild(DxFormComponent)
  dxForm: DxFormComponent | null = null;

  @ContentChildren(PageEditActionComponent)
  actions!: QueryList<PageEditActionComponent>;

  @Input()
  enablePreventLostData: boolean = false;

  @Input()
  mode: "submit" | "realtime" = "submit";

  @Input()
  updateFn: (() => Promise<void>) | null = null;

  @Input()
  patchFn:
    | ((data: Record<string, any>, cancelFn: () => void) => Promise<void>)
    | null = null;

  @Input()
  hideCancelButton: boolean = false;

  @Input()
  preventCloseTabAfterUpdate: boolean = false;

  @Input()
  saveAlwaysEnabled: boolean = false;

  @Input()
  layout: PageEditLayoutType = "default";

  theme = input<"v2" | undefined>(undefined);

  title = input<string | undefined>(undefined);

  subTitle = input<string | undefined>(undefined);

  icon = input<string | "default" | undefined, string | undefined>(undefined, {
    transform: (value: string | "default" | undefined) => {
      switch (value) {
        case "default":
          return "fa-light fa-pen";
        default: {
          return value;
        }
      }
    }
  });

  lastUpdated = input<string | undefined, string | undefined>(undefined, {
    transform: (value: string | undefined) => {
      if (!value) {
        return "";
      }

      return sntTranslate(marker("Ultima modifica il [lastUpdated]"), {
        lastUpdated: dateUtil(value).format("LLL")
      });
    }
  });

  @Input()
  cancelText: string = sntTranslate(marker("Annulla"));

  @Input()
  confirmText: string = sntTranslate(marker("Salva"));

  @Input()
  confirmIcon: string | undefined;

  @Input()
  formData?: Record<string, any>;

  @Input()
  disableDropdown: boolean = false;

  @Input()
  initialValueValueExpr: string | undefined;

  @Output()
  update: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  data: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  formDataStatus: EventEmitter<PageEditFormStatus> = new EventEmitter();

  @Output()
  cancel: EventEmitter<void> = new EventEmitter<void>();

  @ContentChild(PageEditStatusContentDirective)
  pageEditStatusContentTemplateRef?: PageEditStatusContentDirective;

  private optionChangedSubscription: Subscription | undefined;
  private fieldDataChangedSubscription: Subscription | undefined;

  private formChanges: Record<
    string,
    { initialValue: any; dirty: boolean; value?: any }
  > = {};

  private _currentFormStatus: PageEditFormStatus = "pristine";

  set currentFormStatus(value: PageEditFormStatus) {
    if (this.enablePreventLostData) {
      if (value === "dirty" && this._currentFormStatus === "pristine") {
        this.befeoreUnloadService.acquire();
      } else if (value === "pristine" && this._currentFormStatus === "dirty") {
        this.befeoreUnloadService.release();
      }
    }

    this._currentFormStatus = value;
  }

  get currentFormStatus(): PageEditFormStatus {
    return this._currentFormStatus;
  }

  actionItems: Array<{
    id: string;
    name: string;
    action: PageEditActionComponent;
  }> = [];

  updating: boolean = false;

  get isFormPristine(): boolean {
    return this.currentFormStatus === "pristine";
  }

  private readonly befeoreUnloadService = inject(BeforeUnloadService);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  ngAfterContentInit(): void {
    if (this.dxForm) {
      this.buildFormChanges(this.dxForm.formData);

      this.optionChangedSubscription = this.dxForm!.onOptionChanged.subscribe(
        ({ name }: { name: string }) => {
          switch (name) {
            case "items": {
              this.buildFormChanges(this.dxForm?.formData);
            }
          }
        }
      );

      this.fieldDataChangedSubscription =
        this.dxForm?.onFieldDataChanged.subscribe(({ value, dataField }) => {
          this.propertyChanged(dataField, value);
        });
    } else if (this.formData) {
      this.buildFormChanges(this.formData);
    }

    this.actionItems = this.actions.map((x) => ({
      id: nextRuntimeId(),
      name: x.title,
      action: x
    }));
  }

  ngOnDestroy(): void {
    this.optionChangedSubscription?.unsubscribe();
    this.fieldDataChangedSubscription?.unsubscribe();
  }

  buildFormChanges(formData: Record<string, any>): void {
    const formChanges: Record<string, any> = {};
    toPairs(formData).map(([key, value]) => {
      formChanges[key] = {
        initialValue: this.formChanges[key]
          ? this.formChanges[key].initialValue
          : clone(value),
        dirty: this.formChanges[key] ? this.formChanges[key].dirty : false,
        value: this.formChanges[key]?.value
      };
      if (isArray(value)) {
        value.forEach((x, index) => {
          keys(x).forEach((childKey) => {
            const calculatedKey = `${key}[${index}].${childKey}`;
            formChanges[calculatedKey] = {
              initialValue: this.formChanges[calculatedKey]
                ? this.formChanges[calculatedKey].initialValue
                : x[childKey],
              dirty: this.formChanges[calculatedKey]
                ? this.formChanges[calculatedKey].dirty
                : false
            };
          });
        });
      }
    });

    this.formChanges = formChanges;
  }

  async onCancel(): Promise<void> {
    if (this.cancel.observers && this.cancel.observers.length > 0) {
      this.cancel.emit();
    } else {
      await this.bootstrapService.closeCurrentTab();
    }
  }

  async onUpdate(): Promise<void> {
    if (this.dxForm) {
      let validationResult = this.dxForm?.instance.validate();

      if (validationResult.complete) {
        validationResult = await validationResult.complete;
      }

      if (!validationResult.isValid) {
        return;
      }
    }

    let updateFailed = false;

    let updateCanceled: boolean = false;

    const cancelFn = () => {
      updateCanceled = true;
    };

    const updateConsumerFn = this.patchFn
      ? () =>
          this.patchFn
            ? this.patchFn(
                toPairs(this.formChanges)
                  .filter(([key, formChangeHolder]) => formChangeHolder.dirty)
                  .reduce(
                    (map: Record<string, any>, [key, formChangeHolder]) => {
                      map[key] = formChangeHolder.value;
                      return map;
                    },
                    {}
                  ),
                cancelFn
              )
            : Promise.resolve()
      : () => (this.updateFn ? this.updateFn() : Promise.resolve());

    if (this.patchFn) {
      try {
        this.updating = true;

        this.changeDetectorRef.detectChanges();

        await updateConsumerFn();

        if (!updateCanceled) {
          keys(this.formChanges).forEach((key) => {
            this.formChanges[key].dirty = false;
          });

          this.updateFormDataStatus();
        }
      } catch (ex) {
        console.error(ex);
        updateFailed = true;
      } finally {
        this.updating = false;
      }

      this.changeDetectorRef.detectChanges();
    }

    if (this.updateFn) {
      try {
        this.updating = true;
        this.changeDetectorRef.detectChanges();

        await this.updateFn();

        keys(this.formChanges).forEach((key) => {
          this.formChanges[key].dirty = false;
        });

        this.updateFormDataStatus();
      } catch (ex) {
        console.error(ex);
        updateFailed = true;
      } finally {
        this.updating = false;
      }

      this.changeDetectorRef.detectChanges();
    }

    if (!this.preventCloseTabAfterUpdate && !updateFailed) {
      this.bootstrapService.closeCurrentTab();
    }
  }

  saved(): void {
    keys(this.formChanges).forEach((key) => {
      this.formChanges[key].dirty = false;
    });

    this.updateFormDataStatus();
  }

  getFormStatus(): PageEditFormStatus {
    const noDirty = every(
      toPairs(this.formChanges).map(([key, value]) => value.dirty),
      (x) => !x
    );

    return noDirty ? "pristine" : "dirty";
  }

  onDropdownButtonClick(): void {
    this.actions.first.onClick.emit();
  }

  onDropdownButtonItemClick({ itemData }: ItemClickEvent): void {
    itemData.action.onClick.emit();
  }

  propertyChanged(name: string, value: any): void {
    this.data.emit({ dataField: name, value });

    let formChangeHolder = this.formChanges[name];

    if (!formChangeHolder) {
      console.warn(`formChangeHolder undefined, dataField: \"${name}\"`);
      formChangeHolder = this.formChanges[name] = {
        dirty: true,
        initialValue: null
      };
    }

    formChangeHolder.value = value;

    const initialValue = this.initialValueValueExpr
      ? get(
          formChangeHolder.initialValue,
          this.initialValueValueExpr,
          formChangeHolder.initialValue
        )
      : formChangeHolder.initialValue;

    switch (true) {
      case isDate(value): {
        formChangeHolder.dirty = !dateUtil(value).isSame(
          dateUtil(initialValue)
        );
        break;
      }
      case isArray(value): {
        const left = value ? sortBy(value) : value;
        const right = initialValue ? sortBy(initialValue) : initialValue;

        const isDirty = !isEqual(left, right);
        formChangeHolder.dirty = isDirty;
        break;
      }
      default: {
        if (isBoolean(value) && formChangeHolder.dirty) {
          formChangeHolder.dirty = value !== !!initialValue;
        } else {
          formChangeHolder.dirty = value !== initialValue;
        }
      }
    }

    this.updateFormDataStatus();
    this.changeDetectorRef.detectChanges();
  }

  private updateFormDataStatus(): void {
    this.currentFormStatus = this.getFormStatus();
    this.formDataStatus.emit(this.currentFormStatus);

    this.changeDetectorRef.detectChanges();
  }
}
